import React, { useRef, useEffect, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Box from '@mui/material/Box';
import T from 'shared/atoms/Typography';
import theme from 'shared/themes/default';

interface SelectorOption {
  key: string;
  value: string;
}

interface SelectorProps {
  disabled?: boolean;
  onChange: (value: string) => void;
  options: SelectorOption[];
  value?: string;
}

const Selector = ({ disabled, onChange, options, value }: SelectorProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.mobile);
  const [sliderStyle, setSliderStyle] = useState<React.CSSProperties>({});
  const buttonRefs = useRef<Record<string, HTMLButtonElement>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value && buttonRefs.current[value] && containerRef.current) {
      const button = buttonRefs.current[value];
      const container = containerRef.current;
      const buttonRect = button.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      setSliderStyle({
        width: buttonRect.width,
        height: buttonRect.height,
        transform: `translateX(${buttonRect.left - containerRect.left - 4}px)`,
      });
    }
  }, [value, options]);

  const sxButtonGroup = {
    background: theme.palette.darkWrapper,
    borderRadius: '20px',
    padding: theme.spacing.xs,
    width: isMobile ? '100%' : 'auto',
    position: 'relative',
    overflow: 'hidden',
  };

  const sxButton = {
    border: 'none !important',
    borderRadius: '20px !important',
    flex: '1 1 auto',
    margin: '0 !important',
    padding: `${theme.spacing.xs} ${theme.spacing.m1}`,
    textTransform: 'none',
    position: 'relative',
    zIndex: 2,
    backgroundColor: 'transparent !important',
    transition: 'color 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
    },
  };

  const sxSlider = {
    position: 'absolute',
    borderRadius: '20px',
    background: disabled ? theme.palette.disabledDark : theme.palette.brand,
    transition:
      'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 1,
    ...sliderStyle,
  };

  const unselectedColor = disabled
    ? theme.palette.disabledDark
    : theme.palette.lightPure;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-block',
        width: isMobile ? '100%' : 'auto',
      }}
    >
      <ToggleButtonGroup
        ref={containerRef}
        disabled={disabled}
        exclusive
        onChange={(_, newValue: string) => {
          onChange(newValue);
        }}
        sx={sxButtonGroup}
        value={value}
      >
        {value && <Box sx={sxSlider} />}
        {options.map((option) => {
          const isSelected = option.key === value;
          return (
            <ToggleButton
              ref={(el: HTMLButtonElement) => {
                if (el) {
                  buttonRefs.current[option.key] = el;
                }
              }}
              disableRipple
              key={option.key}
              selected={isSelected}
              sx={sxButton}
              value={option.key}
            >
              <T
                color={isSelected ? theme.palette.mainDark : unselectedColor}
                fontWeight={isSelected ? 'semibold' : 'regular'}
                variant="paragraph"
                sx={{ transition: 'all 0.3s ease' }}
              >
                {option.value}
              </T>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
};

export default Selector;
export type { SelectorProps, SelectorOption };
